export interface FriendlyError {
  title: string;
  description: string;
}

interface FriendlyErrorInput {
  errorCode?: string | null;
  errorMessage?: string | null;
  details?: string | null;
  httpStatus?: number | null;
  raw?: unknown;
}

function safeParseJson(value: unknown): any {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function getFriendlyError(input: FriendlyErrorInput): FriendlyError {
  const parsedRaw = safeParseJson(input.raw);
  const parsedDetails = safeParseJson(input.details);

  const code: string = String(
    input.errorCode ||
      parsedRaw?.code ||
      parsedRaw?.error?.code ||
      parsedRaw?.error?.type ||
      parsedDetails?.code ||
      parsedDetails?.error?.code ||
      parsedDetails?.error?.type ||
      ''
  ).toLowerCase();

  const messageRaw: string = String(
    input.errorMessage ||
      parsedRaw?.error ||
      parsedRaw?.message ||
      parsedRaw?.error?.message ||
      ''
  );

  const detailsRaw: string = String(
    input.details ||
      parsedRaw?.details ||
      parsedRaw?.error?.message ||
      parsedDetails?.error?.message ||
      parsedDetails?.message ||
      ''
  );

  const combined = `${messageRaw} ${detailsRaw}`.toLowerCase();

  if (
    code.includes('insufficient_quota') ||
    combined.includes('exceeded your current quota') ||
    combined.includes('insufficient_quota')
  ) {
    return {
      title: 'Limite da OpenAI atingido',
      description:
        'A conta OpenAI vinculada está sem créditos disponíveis. Acesse o painel da OpenAI para verificar seu plano e detalhes de cobrança, depois tente novamente.',
    };
  }

  if (
    code.includes('rate_limit') ||
    combined.includes('rate limit') ||
    input.httpStatus === 429
  ) {
    return {
      title: 'Muitas requisições em pouco tempo',
      description:
        'O limite de requisições por minuto da OpenAI foi excedido. Aguarde alguns segundos e tente novamente.',
    };
  }

  if (
    code.includes('invalid_api_key') ||
    code.includes('openai_token_not_configured') ||
    code.includes('openai_token_missing') ||
    combined.includes('invalid api key') ||
    combined.includes('incorrect api key')
  ) {
    return {
      title: 'Chave da OpenAI inválida',
      description:
        'A chave da OpenAI configurada não é válida ou não foi encontrada. Vá em Configurações → Integrações e revise sua chave.',
    };
  }

  if (code.includes('model_not_found') || combined.includes('model not found')) {
    return {
      title: 'Modelo de IA indisponível',
      description:
        'O modelo solicitado não está disponível para esta chave da OpenAI. Verifique seu plano e permissões na OpenAI.',
    };
  }

  if (code.includes('assistant_not_found')) {
    return {
      title: 'Assistente não encontrado',
      description:
        'Não foi possível localizar este assistente. Salve novamente as configurações e tente outra vez.',
    };
  }

  if (
    code.includes('empty_ai_response') ||
    code.includes('empty_fallback_response') ||
    code.includes('empty_ai_followup_response')
  ) {
    return {
      title: 'Sem resposta da IA',
      description:
        'A OpenAI processou a mensagem, mas não devolveu nenhum texto. Reformule a mensagem e tente novamente.',
    };
  }

  if (code.includes('followup_and_fallback_failed')) {
    return {
      title: 'Falha ao executar ações da IA',
      description:
        'A IA tentou executar webhooks ou ações de calendário, mas não foi possível concluir. Verifique se os webhooks estão respondendo corretamente.',
    };
  }

  if (
    code.includes('db_assistant_fetch_error') ||
    code.includes('db_integration_fetch_error') ||
    code.includes('db_fetch_error')
  ) {
    return {
      title: 'Erro ao consultar banco de dados',
      description:
        'Não foi possível ler as configurações da conta. Tente novamente em instantes; se o problema persistir, contate o suporte.',
    };
  }

  if (code.includes('text_required')) {
    return {
      title: 'Mensagem vazia',
      description:
        'É necessário enviar um texto para processar. Verifique se a mensagem foi preenchida corretamente.',
    };
  }

  if (code.includes('no_pending_messages')) {
    return {
      title: 'Nenhuma mensagem pendente',
      description:
        'Não há mensagens novas para processar neste contato no momento.',
    };
  }

  if (code.includes('supabase_config_missing')) {
    return {
      title: 'Configuração do servidor ausente',
      description:
        'As credenciais do Supabase não estão configuradas no servidor. Contate o administrador.',
    };
  }

  if (code.includes('method_not_allowed')) {
    return {
      title: 'Método não permitido',
      description:
        'A requisição foi enviada de uma forma não suportada. Recarregue a página e tente novamente.',
    };
  }

  if (input.httpStatus === 401 || input.httpStatus === 403) {
    return {
      title: 'Acesso não autorizado',
      description:
        'A chave da OpenAI foi recusada. Verifique se ela está ativa e com permissões corretas em Configurações → Integrações.',
    };
  }

  if (input.httpStatus && input.httpStatus >= 500) {
    return {
      title: 'Erro no servidor de IA',
      description:
        'Tivemos um problema temporário ao processar sua mensagem. Aguarde um instante e tente novamente.',
    };
  }

  if (messageRaw && messageRaw.toLowerCase() !== 'internal server error') {
    return {
      title: messageRaw,
      description:
        detailsRaw ||
        'Ocorreu um erro inesperado. Tente novamente em instantes e, se persistir, consulte o suporte.',
    };
  }

  return {
    title: 'Não foi possível processar sua mensagem',
    description:
      'Ocorreu um erro inesperado. Tente novamente em instantes e, se persistir, consulte os logs para mais detalhes.',
  };
}

/**
 * Traduz erros técnicos vindos da coluna `error_message` em
 * `account_scheduled_messages` (WAHA / Puppeteer / pg_net / cron) numa
 * mensagem curta e amigável em português. Retorna `null` quando não há erro.
 */
export function getFriendlyScheduledMessageError(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const text = String(raw);
  const lower = text.toLowerCase();

  const httpMatch = lower.match(/waha http\s*(\d{3})/);
  const httpStatus = httpMatch ? Number(httpMatch[1]) : null;

  if (
    lower.includes('target closed') ||
    lower.includes('targetcloseerror') ||
    lower.includes('protocol error') ||
    lower.includes('page closed') ||
    lower.includes('session closed')
  ) {
    return 'A conexão com o WhatsApp caiu no momento do envio. Tente reagendar em alguns minutos.';
  }

  if (httpStatus === 401 || httpStatus === 403) {
    return 'Sessão do WhatsApp desconectada. Reconecte sua conta em Configurações → Conexão.';
  }

  if (httpStatus === 404 && lower.includes('session')) {
    return 'Instância do WhatsApp não encontrada. Verifique sua conexão.';
  }

  if (httpStatus === 422 || httpStatus === 400) {
    return 'Mensagem recusada pelo WhatsApp. Verifique o número do contato e o conteúdo.';
  }

  if (httpStatus === 429) {
    return 'Muitas mensagens enviadas em pouco tempo. Aguarde alguns minutos antes de tentar novamente.';
  }

  if (httpStatus && httpStatus >= 500) {
    return 'O servidor do WhatsApp falhou temporariamente. Tente reagendar em alguns minutos.';
  }

  if (lower.includes('pg_net falhou')) {
    return 'Falha ao acionar o envio no servidor. Tente reagendar em alguns minutos.';
  }

  if (lower.includes('timeout aguardando callback')) {
    return 'O envio demorou demais e atingiu o tempo limite. Tente reagendar.';
  }

  if (lower.includes('instância whatsapp não encontrada')) {
    return 'Conecte seu WhatsApp antes de agendar mensagens.';
  }

  if (lower.includes('waha api configuration missing')) {
    return 'O WhatsApp ainda não foi configurado no servidor. Fale com o suporte.';
  }

  if (lower.includes('account_id') && lower.includes('obrigat')) {
    return 'Dados do agendamento estavam incompletos.';
  }

  return 'Falha ao enviar a mensagem agendada. Tente reagendar.';
}
